import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { searchAll, SearchResponse } from '../utils/secureFunctions';
import ImageWithFallback from '../components/ImageWithFallback';
import TopNavigation from '../components/TopNavigation';
import { getProfileUrl } from '../utils/urls';
import '../styles/home.css';
import { trackSearch } from '../utils/activityTracking';

interface SearchResult {
  id: string;
  type: 'user' | 'post' | 'pulse' | 'testimony' | 'group';
  title: string;
  subtitle?: string;
  imageUrl?: string;
  userName?: string;
  username?: string;
  content?: string;
}

const Search: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [searchedTerm, setSearchedTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'users' | 'posts' | 'videos' | 'groups'>('all');
  const requestSeq = useRef(0);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const runSearch = useCallback(async (query: string) => {
    const term = query.trim();
    const seq = ++requestSeq.current;
    if (!term) {
      setResponse(null);
      setSearchedTerm('');
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await searchAll(term, 30);
      if (seq !== requestSeq.current) return;
      setResponse(res);
      setSearchedTerm(term);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('Search failed:', err);
      setSearchedTerm(term);
      setError((err as Error)?.message === 'auth-required' ? 'Please sign in to search.' : 'Search failed. Please try again.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  // Search as the user types (debounced).
  useEffect(() => {
    const term = searchQuery.trim();
    const timer = window.setTimeout(() => {
      runSearch(term);
      setSearchParams(term ? { q: term } : {}, { replace: true });
    }, term ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [searchQuery, runSearch, setSearchParams]);

  const submitSearch = () => {
    const term = searchQuery.trim();
    if (!term) return;
    try { trackSearch(term); } catch {}
    runSearch(term);
  };

  const results = useMemo<SearchResult[]>(() => {
    if (!response) return [];
    const out: SearchResult[] = [];
    if (activeTab === 'all' || activeTab === 'users') {
      response.users.forEach((u) => out.push({
        id: u.id,
        type: 'user',
        title: `${u.name || 'Anonymous'}${u.verified ? ' ✔️' : ''}`,
        subtitle: u.username ? `@${u.username}` : undefined,
        imageUrl: u.photoURL || undefined,
        username: u.username || undefined,
      }));
    }
    if (activeTab === 'all' || activeTab === 'groups') {
      (response.groups || []).forEach((g) => out.push({
        id: g.id,
        type: 'group',
        title: g.name,
        subtitle: `${g.category ? `${g.category} · ` : ''}${g.memberCount} member${g.memberCount === 1 ? '' : 's'}${g.isPrivate ? ' · Private' : ''}`,
        imageUrl: g.coverImage || undefined,
        content: g.description,
      }));
    }
    if (activeTab === 'all' || activeTab === 'posts') {
      response.posts.forEach((p) => out.push({
        id: p.id,
        type: 'post',
        title: p.content.substring(0, 100) + (p.content.length > 100 ? '...' : ''),
        subtitle: `by ${p.userName}`,
        imageUrl: p.imageURL || undefined,
        userName: p.userName,
      }));
    }
    if (activeTab === 'all' || activeTab === 'videos') {
      response.pulses.forEach((p) => out.push({
        id: p.id,
        type: 'pulse',
        title: p.caption || 'Pulse Video',
        subtitle: `by ${p.userName}`,
        userName: p.userName,
      }));
    }
    if (activeTab === 'all' || activeTab === 'posts') {
      response.testimonies.forEach((t) => out.push({
        id: t.id,
        type: 'testimony',
        title: t.title || 'Testimony',
        subtitle: `by ${t.userName}`,
        imageUrl: t.imageURL || undefined,
        userName: t.userName,
        content: t.content?.substring(0, 150),
      }));
    }
    return out;
  }, [response, activeTab]);

  const handleResultClick = (result: SearchResult) => {
    try { trackSearch(searchQuery.trim()); } catch {}
    switch (result.type) {
      case 'user':
        navigate(getProfileUrl({ id: result.id, username: result.username }));
        break;
      case 'post':
        navigate(`/post/${encodeURIComponent(result.id)}`);
        break;
      case 'pulse':
        navigate(`/pulse`);
        break;
      case 'testimony':
        navigate(`/testimony`);
        break;
      case 'group':
        navigate(`/groups/${encodeURIComponent(result.id)}`);
        break;
    }
  };

  const trimmedQuery = searchQuery.trim();
  const emptyMessage = !trimmedQuery
    ? 'Start typing to search people, groups, posts and videos.'
    : loading || searchedTerm !== trimmedQuery
      ? 'Searching…'
      : error || 'No results found. Try different keywords.';

  return (
    <div style={styles.container}>
      <TopNavigation />
      
      <div style={{ ...styles.contentArea, ...(isMobile ? styles.contentAreaMobile : {}) }}>
        <main style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
          <div style={styles.searchHeader}>
            <h1 style={{ ...styles.pageTitle, ...(isMobile ? { fontSize: 22 } : {}) }}>🔍 Search</h1>
            
            <div style={{ ...styles.searchBox, ...(isMobile ? styles.searchBoxMobile : {}) }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
                autoFocus
                enterKeyHint="search"
                placeholder="Search users, posts, videos, groups..."
                style={{ ...styles.searchInput, ...(isMobile ? { fontSize: 15, padding: '10px 12px' } : {}) }}
              />
              <button onClick={submitSearch} style={{ ...styles.searchButton, ...(isMobile ? { padding: '10px 16px', fontSize: 14 } : {}) }}>
                Search
              </button>
            </div>

            <div style={{ ...styles.tabs, ...(isMobile ? { overflowX: 'auto' } : {}) }}>
              <button
                onClick={() => setActiveTab('all')}
                style={{
                  ...styles.tab,
                  ...(activeTab === 'all' ? styles.activeTab : {})
                }}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab('users')}
                style={{
                  ...styles.tab,
                  ...(activeTab === 'users' ? styles.activeTab : {})
                }}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                style={{
                  ...styles.tab,
                  ...(activeTab === 'posts' ? styles.activeTab : {})
                }}
              >
                Posts
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                style={{
                  ...styles.tab,
                  ...(activeTab === 'videos' ? styles.activeTab : {})
                }}
              >
                Videos
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                style={{
                  ...styles.tab,
                  ...(activeTab === 'groups' ? styles.activeTab : {})
                }}
              >
                Groups
              </button>
            </div>
          </div>

          <div style={styles.resultsContainer}>
            {results.length === 0 ? (
              <p style={styles.emptyText}>
                {emptyMessage}
              </p>
            ) : (
              <div style={styles.resultsList}>
                {results.map((result, index) => (
                  <div
                    key={`${result.type}-${result.id}-${index}`}
                    onClick={() => handleResultClick(result)}
                    style={{ ...styles.resultCard, ...(isMobile ? styles.resultCardMobile : {}) }}
                    className="post-card"
                  >
                    {result.imageUrl && (
                      <ImageWithFallback
                        src={result.imageUrl}
                        alt=""
                        style={{ ...styles.resultImage, ...(isMobile ? { width: 72, height: 72 } : {}) }}
                      />
                    )}
                    <div style={styles.resultContent}>
                      <div style={styles.resultType}>{result.type.toUpperCase()}</div>
                      <h3 style={{ ...styles.resultTitle, ...(isMobile ? { fontSize: 16 } : {}) }}>{result.title}</h3>
                      {result.subtitle && (
                        <p style={{ ...styles.resultSubtitle, ...(isMobile ? { fontSize: 12 } : {}) }}>{result.subtitle}</p>
                      )}
                      {result.content && (
                        <p style={{ ...styles.resultDescription, ...(isMobile ? { fontSize: 12 } : {}) }}>{result.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-alt)',
  },
  contentArea: {
    paddingTop: '76px',
    marginLeft: '300px',
    marginRight: '340px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  contentAreaMobile: {
    paddingTop: '12px',
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  main: {
    flex: 1,
    maxWidth: '900px',
    margin: '0 auto',
    padding: '0 0 40px 0',
  },
  mainMobile: {
    paddingBottom: 88,
  },
  searchHeader: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-lg)',
    boxShadow: 'var(--shadow-base)',
    border: '1px solid var(--color-border)',
  },
  searchBoxMobile: {
    flexDirection: 'column',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-md)',
    marginTop: 0,
  },
  searchBox: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-lg)',
  },
  searchInput: {
    flex: 1,
    padding: 'var(--spacing-md)',
    fontSize: '16px',
    borderRadius: 'var(--radius-md)',
    border: '2px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
    transition: 'all var(--transition-fast)',
  },
  searchButton: {
    padding: '10px 32px',
    backgroundColor: 'var(--color-primary-500)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all var(--transition-fast)',
  },
  tabs: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
    borderBottom: '2px solid var(--color-border)',
    paddingBottom: 'var(--spacing-sm)',
  },
  tab: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text-secondary)',
    transition: 'all var(--transition-fast)',
  },
  activeTab: {
    backgroundColor: 'var(--color-primary-50)',
    color: 'var(--color-primary-700)',
  },
  resultsContainer: {
    minHeight: '400px',
  },
  loadingText: {
    textAlign: 'center' as const,
    padding: '40px',
    color: 'var(--color-text-secondary)',
    fontSize: '16px',
  },
  emptyText: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: 'var(--color-text-secondary)',
    fontSize: '16px',
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },
  resultCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-lg)',
    boxShadow: 'var(--shadow-base)',
    border: '1px solid var(--color-border)',
    cursor: 'pointer',
    display: 'flex',
    gap: 'var(--spacing-lg)',
    transition: 'all var(--transition-base)',
  },
  resultCardMobile: {
    padding: '12px',
    gap: '12px',
  },
  resultImage: {
    width: '120px',
    height: '120px',
    borderRadius: 'var(--radius-md)',
    objectFit: 'cover' as const,
    flexShrink: 0,
  },
  resultContent: {
    flex: 1,
    minWidth: 0,
  },
  resultType: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: 'var(--color-primary-100)',
    color: 'var(--color-primary-700)',
    borderRadius: 'var(--radius-full)',
    fontSize: '11px',
    fontWeight: '600',
    marginBottom: 'var(--spacing-sm)',
    textTransform: 'uppercase' as const,
  },
  resultTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-xs)',
    marginTop: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as any,
  },
  resultSubtitle: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-xs)',
  },
  resultDescription: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as any,
  },
};

export default Search;
